import type { ConversationMessage, WorldDocument } from "@private-fiction/story-core";

export interface DemoBot {
  id: string;
  title: string;
  description: string;
  gender: string;
  mode: "novel" | "chat";
  tags: string[];
  tone: string;
  accent: string;
  playCount: number;
  likeCount: number;
  characterCreationEnabled: boolean;
  hasPlayableSession?: boolean;
  intro: string;
}

export const demoBots: DemoBot[] = [
  {
    id: "bot:jura", title: "The Great Jura Forest", gender: "Story", mode: "novel", tone: "forest",
    description: "โลกแฟนตาซีที่ทุกระยะทางมีความหมาย เมืองและผู้คนยังเคลื่อนไหว แม้คุณเลือกยืนดูอยู่ไกล ๆ",
    tags: ["แฟนตาซี", "โลกเปิด", "ต่างโลก", "เนื้อเรื่องเข้มข้น"], accent: "#ff6b35", playCount: 18240, likeCount: 4210,
    characterCreationEnabled: true, hasPlayableSession: true,
    intro: "คุณมาถึงชายขอบของป่าใหญ่จูราในวันที่ฝนกำลังซา ไม่มีคำทำนายบอกว่าคุณต้องเป็นใคร และไม่มีลูกเต๋าบังคับว่าคุณต้องเดินไปทางไหน",
  },
  {
    id: "bot:after-rain", title: "After the Last Rain", gender: "Character", mode: "chat", tone: "romance",
    description: "ร้านหนังสือเล็ก ๆ การพบกันซ้ำ ๆ และคำที่ไม่มีใครกล้าพูดก่อนฤดูฝนจะจบ",
    tags: ["โรแมนติก", "Slow burn", "ชีวิตประจำวัน"], accent: "#ef7c76", playCount: 11290, likeCount: 3890,
    characterCreationEnabled: false, intro: "กลิ่นกระดาษเก่าและกาแฟดำติดอยู่ในอากาศ เธอเงยหน้าจากหนังสือเมื่อกระดิ่งหน้าร้านดังขึ้น",
  },
  {
    id: "bot:glass-moon", title: "City Beneath a Glass Moon", gender: "World", mode: "novel", tone: "violet",
    description: "นครเวทมนตร์ที่ความทรงจำซื้อขายได้ และทุกคนเก็บชื่อจริงไว้ในสถานที่ซึ่งไม่มีแผนที่",
    tags: ["Mystery", "แฟนตาซี", "เมืองเวทมนตร์"], accent: "#9f83d7", playCount: 8760, likeCount: 2260,
    characterCreationEnabled: true, intro: "ดวงจันทร์แก้วแตกเป็นแสงเจ็ดสีเหนือหลังคาเมือง ขณะที่นายหน้าความทรงจำคนแรกเรียกชื่อซึ่งคุณไม่เคยบอกใคร",
  },
  {
    id: "bot:signal-zero", title: "Signal Zero", gender: "Story", mode: "novel", tone: "signal",
    description: "สถานีอวกาศร้าง สัญญาณที่ไม่ควรมีผู้ส่ง และลูกเรือซึ่งอาจไม่ใช่มนุษย์ครบทุกคน",
    tags: ["Sci-fi", "เอาตัวรอด", "ลึกลับ"], accent: "#46b8da", playCount: 7310, likeCount: 1940,
    characterCreationEnabled: true, intro: "เมื่อระบบสำรองติดขึ้น หน้าต่างทุกบานสะท้อนดาวที่ไม่อยู่ในแผนที่ และเสียงประกาศเรียกจำนวนลูกเรือเกินมาหนึ่งคน",
  },
  {
    id: "bot:red-court", title: "The Red Court", gender: "Character", mode: "chat", tone: "court",
    description: "การเมือง คำสาบาน และความสัมพันธ์อันตรายในราชสำนักที่รอยยิ้มคืออาวุธชิ้นแรก",
    tags: ["การเมือง", "โรแมนติก", "ดราม่า"], accent: "#c75b4e", playCount: 6900, likeCount: 2510,
    characterCreationEnabled: false, intro: "เสียงดนตรีหยุดลงเมื่อคุณก้าวเข้าสู่ท้องพระโรง ทุกสายตาหันมา ยกเว้นคนเพียงคนเดียวที่คุณตั้งใจมาพบ",
  },
];

const now = "2026-09-01T10:20:00.000Z";

export const demoDocuments: WorldDocument[] = [
  {
    id: "character:kaelen",
    storyId: "story:jura",
    kind: "character",
    schemaVersion: 1,
    revision: 7,
    title: "Kaelen the Silent Warden",
    summary: "นักเดินทางผู้ดูไม่มีพิษภัย ซึ่งแบกมรดกของผู้พิทักษ์โบราณไว้ภายใน",
    fields: {
      epithet: "THE SILENT WARDEN",
      state: "Active",
      threat: "Below Hazard",
      level: 1,
      hp: 140,
      maxHp: 4900,
      mp: 160,
      maxMp: 1120,
      image: "",
      conditions: [],
      currentObjective: "ตามหาร่องรอยของหอคอยสื่อสารแห่งแรก โดยไม่เปิดเผยตัวตน",
    },
    references: [
      { relation: "current_location", targetId: "location:jura-road" },
      { relation: "equipped", targetId: "item:warden-cloak" },
      { relation: "knows", targetId: "skill:aegis-bestiarium" },
    ],
    visibility: "partial",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "location:jura-road",
    storyId: "story:jura",
    kind: "location",
    schemaVersion: 1,
    revision: 3,
    title: "ถนนใต้เรือนยอดแห่งจูรา",
    summary: "ถนนป่าชื้นที่ทอดเข้าสู่เขตของ Jura Tempest Federation",
    fields: { region: "The Great Jura Forest", weather: "ฝนใบไม้ผลิกำลังซา", danger: "สงบผิดปกติ", x: 0.663, y: 0.575 },
    references: [{ relation: "inside", targetId: "location:jura-forest" }],
    visibility: "pinned",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "item:warden-cloak",
    storyId: "story:jura",
    kind: "item",
    schemaVersion: 1,
    revision: 1,
    title: "Warden's Plain Traveler's Cloak",
    summary: "เสื้อคลุมเดินทางธรรมดาที่ตั้งใจทำให้เจ้าของกลืนไปกับฝูงชน",
    fields: { category: "Clothing", rarity: "Common", slot: "Body", equipped: true, quantity: 1, image: "", tags: ["travel", "concealment"] },
    references: [{ relation: "owned_by", targetId: "character:kaelen" }],
    visibility: "searchable",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "skill:aegis-bestiarium",
    storyId: "story:jura",
    kind: "skill",
    schemaVersion: 1,
    revision: 1,
    title: "Aegis Bestiarium",
    summary: "มรดกหลับใหลที่ทำให้แนวคิดของการพิทักษ์มีชีวิตและเจตจำนงของตนเอง",
    fields: { category: "Ultimate Skill", range: "Variable", cost: 0, image: "", tags: ["guardian", "summoning"] },
    references: [{ relation: "owned_by", targetId: "character:kaelen" }],
    visibility: "partial",
    createdAt: now,
    updatedAt: now,
  },
  {
    id: "world-clock:jura",
    storyId: "story:jura",
    kind: "world_clock",
    schemaVersion: 1,
    revision: 4,
    title: "Cardinal World Clock",
    summary: "เวลาปัจจุบันของเรื่อง",
    fields: { calendar: "Thawmoon", day: 10, year: 302, time: "09:30", period: "สาย", weather: "ฝนโปรยเบา" },
    references: [],
    visibility: "pinned",
    createdAt: now,
    updatedAt: now,
  },
];

export const demoMessages: ConversationMessage[] = [
  { id: "m1", sequence: 1, role: "assistant", content: "ฝนฤดูใบไม้ผลิเคาะลงบนเรือนยอดเหนือศีรษะ ก่อนสลายเป็นละอองสีทองเมื่อผ่านม่านพลังเวทของป่าใหญ่จูรา ถนนเบื้องหน้าว่างเปล่าเกินกว่าจะเรียกว่าสงบ" },
  { id: "m2", sequence: 2, role: "user", content: "คาเอลหยุดใต้ต้นไม้ใหญ่ ลดฮู้ดลงเล็กน้อย แล้วฟังเสียงรอบตัวโดยไม่เดินเข้าไปทันที" },
  { id: "m3", sequence: 3, role: "assistant", content: "เมื่อเสียงฝนบางลง คุณจึงได้ยินสิ่งที่ไม่เข้าพวก—เสียงกระดิ่งโลหะสามครั้งจากทิศตะวันออก ตามด้วยเสียงล้อเกวียนซึ่งหยุดกะทันหัน บนดินโคลนมีรอยเท้าสัตว์ขนาดใหญ่ แต่ทุกก้าวหันปลายกลับเข้าหาป่า ราวกับมันเดินถอยหลังมาตลอดทาง" },
];
